import type {IModel} from "../../../data/models/Model";
import type {XyiconFeature} from "../../../generated/api/base";
import type {spaceItemForProperties} from "../../modules/abstract/sidepanel/tabs/details/field/mass/IMassInput";
import type {SpaceItem} from "../../modules/space/spaceeditor/logic3d/elements3d/SpaceItem";

interface IPropertiesItemManager {
	readonly items: {
		readonly array: SpaceItem[];
	};
}

interface IPropertiesSpaceViewRenderer {
	readonly spaceItemController: {
		readonly selectedItems: SpaceItem[];
	};
	getItemManager(feature: XyiconFeature): IPropertiesItemManager | null;
}

const isSpaceItemForProperties = (spaceItem: SpaceItem): spaceItem is spaceItemForProperties =>
	spaceItem.spaceItemType === "xyicon" || spaceItem.spaceItemType === "boundary" || spaceItem.spaceItemType === "markup";

const doesSpaceItemMatchDetailsItem = (spaceItem: SpaceItem, detailsItem: IModel) => {
	const modelData = spaceItem.modelData as IModel & {
		readonly isBoundarySpaceMap?: boolean;
		readonly parent?: IModel;
	};

	return modelData?.id === detailsItem.id || (modelData?.isBoundarySpaceMap && modelData.parent?.id === detailsItem.id);
};

/**
 * Resolve the 3D objects represented by a details panel without accidentally using
 * an unrelated selection from the Space Editor behind it.
 *
 * Matching selected objects are preferred so selecting multiple space maps of the
 * same boundary remains a mass edit. Otherwise, resolve the objects currently
 * loaded by the renderer (organization-search details are not necessarily selected).
 */
export const getSpaceItemsForDetails = (detailsItems: IModel[], spaceViewRenderer: IPropertiesSpaceViewRenderer) => {
	const selectedItems = spaceViewRenderer.spaceItemController.selectedItems.filter(isSpaceItemForProperties);
	const matchingSelectedItems = selectedItems.filter((spaceItem) =>
		detailsItems.some((detailsItem) => doesSpaceItemMatchDetailsItem(spaceItem, detailsItem)),
	);
	const selectionCoversEveryDetailsItem = detailsItems.every((detailsItem) =>
		matchingSelectedItems.some((spaceItem) => doesSpaceItemMatchDetailsItem(spaceItem, detailsItem)),
	);

	if (selectionCoversEveryDetailsItem) {
		return matchingSelectedItems;
	}

	const itemManagers = Array.from(new Set(detailsItems.map((item) => item.ownFeature)))
		.map((feature) => spaceViewRenderer.getItemManager(feature))
		.filter((itemManager): itemManager is IPropertiesItemManager => !!itemManager);

	return itemManagers
		.flatMap((itemManager) => itemManager.items.array)
		.filter(isSpaceItemForProperties)
		.filter((spaceItem) => detailsItems.some((detailsItem) => doesSpaceItemMatchDetailsItem(spaceItem, detailsItem)));
};
